import { debounce } from 'lodash';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';

import { Card, Table, TableBody, TableContainer, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Stack, useMediaQuery } from '@mui/system';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { PageOptions, SortConfig } from 'src/services/api.types';
import { useCreateDirectInviteMutation, useGetOrganizationCandidatesQuery, useRemoveCandidateMutation, useUnlinkCandidateMutation, useUpdateCandidateRatingMutation } from 'src/services/candidates/candidates.service';
import { CandidateAvailabilityStatus, IOrgCandidate } from 'src/services/candidates/candidates.types';
import { exportOrgCandidateProfile, generateDirectCandidateInviteMessage, generateRemoveCandidateErrorMessage, generateUnlinkCandidateErrorMessage } from 'src/services/candidates/candidates.utils';
import { ListEntityType } from 'src/services/lists/lists.types';
import { openMultiMailerDialog, openSingleMailerDialog } from 'src/store/slices/app/appSlice';
import { toggleCandidateEditorSidesheet, toggleCandidateInviteModal } from 'src/store/slices/candidate/candidateSlice';
import { useAppDispatch } from 'src/store/store';

import Scrollbar from 'src/components/scrollbar';
import {
  emptyRows,
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import CandidatesTableToolbar from 'src/sections/candidates/table/candidate-table-toolbar';
import AddCandidateJobModal from 'src/sections/jobs/modals/add-candidate-job-modal';
import BulkAddCandidatesJobModal from 'src/sections/jobs/modals/bulk-add-candidate-job-modal';
import AddItemsModal from 'src/sections/lists/modals/add-items-modal';

import { TenantType } from 'src/types/enums';

import { LoadingButton } from '@mui/lab';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CandidateImporter from 'src/sections/candidates/modal/candidate-importer';
import { EmailMessageTemplateType, MailerTargetType } from 'src/services/email-sync/email-sync.types';
import { SortOrder } from 'src/services/common.types';
import BulkCandidateImportModal from 'src/sections/candidates/modal/bulk-candidate-import-modal';
import { CandidateTableRow } from './candidate-table-row';

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 10,
};

export default function CandidatesTable() {

  const table = useTable();

  const theme = useTheme();

  const dispatch = useAppDispatch();

  const addCandidateJobControl = useBoolean();

  const bulkImportControl = useBoolean();

  const bulkAddCandidateJobControl = useBoolean();

  const importCandidateControl = useBoolean();

  const addToListControl = useBoolean();

  const unlinkCandidateConfirm = useBoolean();

  const removeCandidateConfirm = useBoolean();

  const authContext = useAuthContext();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const tenant = useOrgTenant();

  const { t } = useTranslate();

  const denseHeight = table.dense ? 56 : 56 + 20;

  const isCandidate = tenant === TenantType.Candidate;

  const tableLabels = useMemo(
    () => [

      { id: 'name', label: t('candidates.table.name') },
      { id: 'email', label: t('candidates.table.email') },
      { id: 'phone', label: t('candidates.table.phone') },
      ...tenant === TenantType.Recruiter ? [{ id: 'availablity', label: t('candidates.table.availability') }] : [],
      { id: 'rating', label: t('candidates.table.rating') },
      { id: 'applications', label: t('candidates.table.job_applications_count') },
      { id: 'actions', label: '' }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCandidate, tenant]
  );

  const [pageOptions, setPageOptions] = useState<PageOptions>(DEFAULT_PAGE);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ sort_by: 'created_at', sort_order: SortOrder.DESC });
  const [filterQuery, setFilterQuery] = useState<string | undefined>();
  const [filterSkills, setFilterSkills] = useState<string[]>([]);
  const [filterAvailability, setFilterAvailability] = useState<CandidateAvailabilityStatus | undefined>();
  const [filterLabels, setFilterLabels] = useState<string[]>([]);

  const [currentCandidate, setCurrentCandidate] = useState<string | undefined>();
  const [selectedCandidates, setSelectedCandidates] = useState<IOrgCandidate[]>([]);

  const [updateCandidateRating] = useUpdateCandidateRatingMutation();

  const [createDirectInvite] = useCreateDirectInviteMutation();
  const [unlinkCandidate, { isLoading: isUnlinking }] = useUnlinkCandidateMutation();
  const [removeCandidate, { isLoading: isRemoving }] = useRemoveCandidateMutation();

  const { currentData: candidates, isLoading: isCandidatesLoading } = useGetOrganizationCandidatesQuery(
    {
      search_query: filterQuery,
      availability_status: filterAvailability,
      labels: filterLabels.length ? filterLabels : undefined,
      skills: filterSkills.length ? filterSkills : undefined,
      page: pageOptions,
      sortConfig,
    },
    {
      skip: !authContext.organization?.id,
      refetchOnMountOrArgChange: true
    }
  );

  const onChangePage = useCallback((_event: any, pageNumber: number) => {
    setPageOptions((prev) => ({
      ...prev,
      page: pageNumber + 1,
    }));
  }, []);

  const onChangeRowsPerPage = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPageOptions((prev) => ({
        ...prev,
        per_page: +event.target.value,
      }));
    },
    []
  );

  const handleFilters = useMemo(
    () => debounce((filter, value: string | TenantType | string[]) => {
      if (filter === 'search_query') {
        setFilterQuery(value as string);
      }
      else if (filter === 'skills') {
        setFilterSkills(value as string[]);
      }
      else if (filter === 'availability_status') {
        setFilterAvailability(value as CandidateAvailabilityStatus);
      }
      else if (filter === 'labels') {
        setFilterLabels(value as string[]);
      }

      setPageOptions((prev) => ({
        ...prev,
        page: 1, // Reset to first page on filter change
      }));
    }, 500),
    [setFilterQuery]
  );

  const clearFilters = useCallback(() => {
    setFilterQuery('');
    setFilterSkills([]);
    setFilterLabels([]);
    setFilterAvailability(undefined);
  }, []);

  const notFound = !candidates?.count;

  const openAddCandidateJobModal = useCallback((candidateId: string) => {
    setCurrentCandidate(candidateId);
    addCandidateJobControl.onTrue();
  }, [addCandidateJobControl]);

  const closeAddCandidateJobModal = useCallback(() => {
    setCurrentCandidate(undefined);
    addCandidateJobControl.onFalse();
  }, [addCandidateJobControl]);

  const handleOpenInviteCandidatesModal = useCallback(() => {
    dispatch(toggleCandidateInviteModal({
      status: true
    }));
  }, [dispatch]);

  const handleCreateCandidateSidesheet = useCallback(() => {
    dispatch(toggleCandidateEditorSidesheet({
      status: true
    }));
  }, [dispatch]);

  const handleRatingUpdate = useCallback(async (candidateId: string, rating: number) => {
    const toastId = enqueueSnackbar(t('candidates.api.update_rating.loading'), {
      variant: 'loading',
      persist: true
    });

    try {
      await updateCandidateRating({
        candidateId,
        rating
      }).unwrap()

      closeSnackbar(toastId);
      enqueueSnackbar(t('candidates.api.update_rating.success'), { variant: 'success' });
    }
    catch (error) {

      closeSnackbar(toastId);
      enqueueSnackbar(t('candidates.api.update_rating.default_error'), { variant: 'error' });
    }
  }, [t, updateCandidateRating]);

  const handleDirectInvite = useCallback(async (candidateId: string) => {
    try {
      await createDirectInvite({
        candidateId
      }).unwrap();

      enqueueSnackbar(t('candidates.api.create_direct_invite.success'), { variant: 'success' });
    }
    catch (error) {
      const errMsg = generateDirectCandidateInviteMessage(error);
      enqueueSnackbar(errMsg, { variant: 'error' });
    }
  }, [t, createDirectInvite]);

  const handleBulkAddToJob = useCallback((candidateIds: string[]) => {
    const selection = candidates?.results.filter((candidate) => candidateIds.includes(candidate.id as string)) ?? [];
    setSelectedCandidates(selection);
    bulkAddCandidateJobControl.onTrue();
  }, [bulkAddCandidateJobControl, candidates?.results]);

  const handleAddToList = useCallback(async (companyIds: string[]) => {
    addToListControl.onTrue();
  }, [addToListControl]);

  const onSuccessAddToList = useCallback(() => {
    table.onSelectAllRows(false);
    addToListControl.onFalse();
  }, [addToListControl, table]);

  const handleOpenMailer = useCallback((candidate: IOrgCandidate) => {
    dispatch(openSingleMailerDialog({
      emails: [candidate.email],
      templateType: EmailMessageTemplateType.CANDIDATE_ENGAGEMENT,
      templateEntityId: candidate.id,
      targetType: MailerTargetType.CANDIDATE,
      targetId: candidate.id
    }));
  }, [dispatch]);

  const openUnlinkConfirm = useCallback((candidateId: string) => {
    setCurrentCandidate(candidateId);
    unlinkCandidateConfirm.onTrue();
  }, [unlinkCandidateConfirm]);

  const openRemoveCandidateConfirm = useCallback((candidateId: string) => {
    setCurrentCandidate(candidateId);
    removeCandidateConfirm.onTrue();
  }, [removeCandidateConfirm]);

  const handleUnlinkCandidate = useCallback(async () => {
    if (!currentCandidate) return;

    try {
      await unlinkCandidate(currentCandidate).unwrap();

      enqueueSnackbar(t('candidates.api.unlink_candidate.success'), { variant: 'success' });
      setCurrentCandidate(undefined);
      unlinkCandidateConfirm.onFalse();
    }
    catch (error) {
      enqueueSnackbar(generateUnlinkCandidateErrorMessage(error), { variant: 'error' });
    }
  }, [currentCandidate, t, unlinkCandidate, unlinkCandidateConfirm]);

  const handleRemoveCandidate = useCallback(async () => {
    if (!currentCandidate) return;

    try {
      await removeCandidate(currentCandidate).unwrap();

      enqueueSnackbar(t('candidates.api.remove_candidate.success'), { variant: 'success' });
      setCurrentCandidate(undefined);
      removeCandidateConfirm.onFalse();
    }
    catch (error) {
      enqueueSnackbar(generateRemoveCandidateErrorMessage(error), { variant: 'error' });
    }

  }, [currentCandidate, t, removeCandidate, removeCandidateConfirm]);


  const handleSendMultiEmail = useCallback(() => {
    if (table.selected.length === 0) return;

    dispatch(openMultiMailerDialog({
      targetType: MailerTargetType.CANDIDATE,
      templateType: EmailMessageTemplateType.CANDIDATE_ENGAGEMENT,
      entities: table.selected,
    }));

  }, [dispatch, table]);

  const handleSortChange = useCallback((config: SortConfig) => {
    setSortConfig(config);
  }, []);

  return (
    <>
      <Card sx={{ borderRadius: 0 }}>
        {
          isCandidatesLoading ? (
            <TableSkeleton />
          )
            :
            <>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <CandidatesTableToolbar
                  table={table}
                  onFilters={handleFilters}
                  onSortChange={handleSortChange}
                  clearFilters={clearFilters}
                  onInviteCandidates={handleOpenInviteCandidatesModal}
                  onCreateCandidate={handleCreateCandidateSidesheet}
                  onImportCandidate={importCandidateControl.onTrue}
                  onBulkAddToJob={handleBulkAddToJob}
                  onAddToList={handleAddToList}
                  onSendMultiEmail={handleSendMultiEmail}
                  onBulkImport={bulkImportControl.onTrue}
                />
              </Stack>

              <TableContainer sx={{ overflow: 'unset' }}>
                <Scrollbar>
                  <Table size={table.dense ? 'small' : 'medium'}>

                    {
                      !isMobile && (
                        <TableHeadCustom
                          headLabel={tableLabels}
                          rowCount={candidates?.results.length ?? 0}
                          numSelected={table.selected.length}
                          onSelectAllRows={(checked) =>
                            table.onSelectAllRows(
                              checked,
                              (candidates?.results ?? []).map((row) => row.id) as string[]
                            )
                          }
                        />
                      )
                    }

                    <TableBody>
                      {candidates?.results?.map((row) =>
                        <CandidateTableRow
                          key={row.id}
                          row={row}
                          onAddToJob={openAddCandidateJobModal}
                          onExportProfile={exportOrgCandidateProfile}
                          onRatingUpdate={handleRatingUpdate}
                          onInvite={handleDirectInvite}
                          selected={table.selected.includes(row.id as string)}
                          onSelectRow={() => table.onSelectRow(row.id as string)}
                          onSendEmail={handleOpenMailer}
                          onUnlink={openUnlinkConfirm}
                          onRemove={openRemoveCandidateConfirm}
                        />)}

                      <TableEmptyRows
                        height={denseHeight}
                        emptyRows={emptyRows(table.page, table.rowsPerPage, candidates?.count ?? 0)}
                      />

                      <TableNoData notFound={notFound} />
                    </TableBody>
                  </Table>
                </Scrollbar>
              </TableContainer>

              <TablePaginationCustom
                count={candidates?.count ?? 0}
                page={pageOptions.page}
                rowsPerPage={pageOptions.per_page}
                onPageChange={onChangePage}
                onRowsPerPageChange={onChangeRowsPerPage}
                dense={table.dense}
                onChangeDense={table.onChangeDense}
              />
            </>
        }
      </Card>

      <AddCandidateJobModal open={addCandidateJobControl.value} onClose={closeAddCandidateJobModal} candidateId={currentCandidate as string} />

      <BulkAddCandidatesJobModal open={bulkAddCandidateJobControl.value} onClose={bulkAddCandidateJobControl.onFalse} candidates={table.selected} />

      <CandidateImporter open={importCandidateControl.value} onClose={importCandidateControl.onFalse} />

      <AddItemsModal
        open={addToListControl.value}
        onClose={addToListControl.onFalse}
        onSuccess={onSuccessAddToList}
        items={table.selected}
        entityType={ListEntityType.CANDIDATE}
      />

      <ConfirmDialog
        open={unlinkCandidateConfirm.value}
        onClose={unlinkCandidateConfirm.onFalse}
        title={t('candidates.unlink_candidate.title')}
        content={
          <Stack spacing={1}>
            <Typography variant="subtitle2" gutterBottom>
              {t('candidates.unlink_candidate.confirm_message_1')}
            </Typography>
            <Typography variant="body2">
              {t('candidates.unlink_candidate.confirm_message_2')}
            </Typography>
          </Stack>
        }
        action={
          <LoadingButton loading={isUnlinking} variant="contained" color="error" onClick={handleUnlinkCandidate}>
            {t('common.unlink')}
          </LoadingButton>
        }
      />

      <ConfirmDialog
        open={removeCandidateConfirm.value}
        onClose={removeCandidateConfirm.onFalse}
        title={t('candidates.remove_candidate.title')}
        content={
          <Stack spacing={1}>
            <Typography variant="body2" gutterBottom>
              {t('candidates.remove_candidate.confirm_message')}
            </Typography>
          </Stack>
        }
        action={
          <LoadingButton loading={isRemoving} variant="contained" color="error" onClick={handleRemoveCandidate}>
            {t('common.remove')}
          </LoadingButton>
        }
      />

      <BulkCandidateImportModal open={bulkImportControl.value} onClose={bulkImportControl.onFalse} />

    </>
  );
}
